import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import api, { apiMediaUrl } from '../api/api';
import { ChevronLeft, ChevronRight, ChevronDown, ArrowLeft, Mic, Loader2, Code2, Briefcase, Shuffle, Clock, Sparkles, Mars, Venus, Users, Megaphone } from 'lucide-react';
import MockInterviewSkeleton from '../components/layout/MockInterviewSkeleton';
import { scoreTone, PILL_LIGHT, PILL_DARK, BAR } from '../utils/resumeReport';
import {
  CATEGORY_LABELS_HISTORY, categoryTextColor, normalizeHistorySession,
  interviewRating, interviewHeadline, categoryBreakdownRows, normalizeResponseScore,
  starComponentRows,
} from '../components/mockInterview/interviewShared';
import StatusBadge from '../components/mockInterview/StatusBadge';
import AudioPlayer from '../components/mockInterview/AudioPlayer';
import MicTester from '../components/mockInterview/MicTester';
import { SetupStepCard, SquareOptionCard } from '../components/mockInterview/SetupCards';
import InterviewModal from '../components/mockInterview/InterviewModal';
// eslint-disable-next-line no-unused-vars -- kept for fallback, swap back into the render if needed
import ElevatorPitchComingSoon from '../components/mockInterview/ElevatorPitchComingSoon';
import PitchSetupTab from '../components/mockInterview/PitchSetupTab';
import PitchDetailView from '../components/mockInterview/PitchDetailView';
import { normalizePitchSession } from '../components/mockInterview/pitchShared';

// Circumference of the r=76 STAR-breakdown donut (on-screen + PDF export).
const STAR_DONUT_C = 2 * Math.PI * 76;

export default function MockInterview() {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(true);
  const [attemptsLoading, setAttemptsLoading] = useState(true); // first-load only; refetches keep the last known number
  const navigate = useNavigate();



  const [sessions, setSessions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [usage, setUsage] = useState();

  const [selectedSession, setSelectedSession] = useState(null);
  const [historyKind, setHistoryKind] = useState('interview'); // 'interview' | 'pitch'

  const [view, setView] = useState('main'); // 'main' | 'elevatorPitch' | 'history'
  const [debouncedView, setDebouncedView] = useState('main')
  const isHistoryTab = debouncedView === 'history'; // only tab that actually fetches sessions
  const isMainTabImmediate = view === 'main'; // drives the button highlight
  const isElevatorPitchTabImmediate = view === 'elevatorPitch';
  
  const perPage = 10;

  const handleViewChange = (v) => {
      setView(v);
      setPage(1);
  };

  // Paginating the History list (Mock Interview or Elevator Pitch) replaces the
  // whole card in place — scroll back up so the user sees the new page from the top.
  const goToPage = (newPage) => {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tabButtonClass = (active) => `px-2 sm:px-4 py-2 rounded-lg text-[11px] sm:text-sm font-medium transition-colors flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-center ${
      active
          ? 'bg-primary-600 text-white'
          : isDarkMode
              ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              : 'bg-white text-slate-600 hover:bg-slate-100'
  }`;
  
  useEffect(() => {
    setSelectedSession(null); // drop back to the list when switching tabs
    if (view === 'history') {
      setLoading(true);
      setInitialLoad(true);
    } else {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [view])

  useEffect(() => {
    if (!user) {
        navigate('/login');
    }
  }, [navigate, user]);

  const fetchHistorySessions = useCallback(async ({ silent = false } = {}) => {
    if (!user) {
      return;
    }
    if (!isHistoryTab) {
      setLoading(false);
      setInitialLoad(false);
      return;
    }
    try {
      if (!silent) setLoading(true);
      const params = { page, per_page: perPage };

      const endpoint = historyKind === 'pitch' ? '/pitch/sessions' : '/interview/sessions';
      const normalizer = historyKind === 'pitch' ? normalizePitchSession : normalizeHistorySession;
      const response = await api.get(endpoint, { params });
      const rows = response.data?.sessions || [];

      const normalized = rows.map(normalizer);

      setSessions(normalized);
      const responseTotal = response.data?.total || 0;
      const responsePerPage = response.data?.per_page || perPage;
      setTotalPages(Math.max(1, Math.ceil(responseTotal / responsePerPage)));
      setTotal(responseTotal);
    } catch (err) {
        console.error('Error fetching history sessions:', err);
        if (!silent) {
          setSessions([]);
          setTotalPages(1);
          setTotal(0);
        }
    } finally {
        if (!silent) {
          setLoading(false);
          setInitialLoad(false);
        }
    }
      }, [page, perPage, user, isHistoryTab, historyKind]);

  useEffect(() => {
    setPage(1);
    setSelectedSession(null);
    setLoading(true);
    setInitialLoad(true);
  }, [historyKind]);

  useEffect(() => {
    fetchHistorySessions();
  }, [fetchHistorySessions]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const interviewCh = new BroadcastChannel('interview-sessions');
    const pitchCh = new BroadcastChannel('pitch-sessions');
    const onMsg = (e) => { if (e.data?.type === 'session-ended') fetchHistorySessions(); };
    interviewCh.onmessage = onMsg;
    pitchCh.onmessage = onMsg;
    return () => { interviewCh.close(); pitchCh.close(); };
  }, [fetchHistorySessions]);

  const fetchRemainingAttempts = useCallback(async () => {
    if (!user) {
      return;
    }

    setAttemptsLoading(true);
    try {
      const endpoint = view === 'elevatorPitch' ? '/pitch/attempts' : '/interview/attempts';
      const response = await api.get(endpoint);

      setUsage(response.data || null);
    } catch (err) {
        console.error('Error fetching remaining attempts:', err);
        setUsage(null)
    } finally {
        setAttemptsLoading(false);
    }
      }, [user, view]);

  useEffect(() => {
    fetchRemainingAttempts();
  }, [fetchRemainingAttempts]);

  // debounce rapid tab switches — apply the view (and refetch) only after 0.5s of no change
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedView(view);
      setPage(1);
      fetchRemainingAttempts();
    }, 500);

    return () => clearTimeout(timer);
  }, [view, fetchRemainingAttempts]);

  const kindToggle = (
    <div className={`flex items-center gap-2`}>
      {[
        { key: 'interview', label: 'Mock Interview' },
        { key: 'pitch', label: 'Elevator Pitch' },
      ].map((k) => (
        <button
          key={k.key}
          onClick={() => {
            setPage(1);
            setHistoryKind?.(k.key)
          }}
          className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
            historyKind === k.key
              ? 'bg-primary-600 text-white'
              : isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {k.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
      <header className="mb-5 md:mb-8">
        <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight mb-1 md:mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Mock Interview</h1>
        <p className={`text-sm md:text-base ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Practice playground to boost your confidence and secure your dream jobs</p>
      </header>

      <div className="grid grid-cols-3 sm:flex gap-2 mb-6">
        <button onClick={() => handleViewChange('main')} className={tabButtonClass(isMainTabImmediate)}>
          <Mic size={16} />
          Mock Interview
        </button>
        <button onClick={() => handleViewChange('elevatorPitch')} className={tabButtonClass(isElevatorPitchTabImmediate)}>
          <Megaphone size={16} />
          Elevator Pitch
        </button>
        <button onClick={() => handleViewChange('history')} className={tabButtonClass(view === 'history')}>
          <Clock size={16} />
          History Sessions
        </button>
      </div>

      {(view === 'history' || usage || attemptsLoading) && (
        <div className={`flex items-center ${view === 'history' ? 'justify-between' : 'justify-end'} text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
          {view === 'history' && (
            kindToggle
          )}
          <div className={`flex items-center gap-2 mb-4`}>
          {/* The History tab doesn't consume attempts, so only the Upgrade prompt stays there. */}
          {view !== 'history' && (
            <span><span className="font-medium">{attemptsLoading || !usage ? '-' : usage.remaining}</span> of {attemptsLoading || !usage ? '-' : usage.maxAttempts} analyses remaining this month</span>
          )}
          <Link
              to="/membership/plans"
              className={`font-semibold whitespace-nowrap px-3 py-1 rounded-full transition-colors ${isDarkMode ? "text-primary-200 bg-primary-600 hover:bg-primary-500" : "text-primary-700 bg-primary-100 hover:bg-primary-200"}`}
          >
              Upgrade Now
          </Link>
          </div>
        </div>
      )}

      <div className={`rounded-md border shadow-sm overflow-hidden mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        {/* Swap the block below for <ComingSoonDisplay isDarkMode={isDarkMode}/> to gate this feature. */}

        {/* Full skeleton only on the very first load (no cached count yet); a tab
            switch keeps the tab mounted and shows '-' for the count while it refetches. */}
        {((loading && initialLoad) || (isMainTabImmediate && attemptsLoading && !usage)) && (
          <MockInterviewSkeleton isDarkMode={isDarkMode} isMainTab={isMainTabImmediate} />
        )}

        {!initialLoad && (
          isMainTabImmediate
            ? ((!attemptsLoading || usage) && <MainTab isDarkMode={isDarkMode} setView={setView} setHistoryKind={setHistoryKind} remainingAttempts={attemptsLoading || !usage ? null : usage.remaining}/>)
            : isElevatorPitchTabImmediate
              ? ((!attemptsLoading || usage) && <PitchSetupTab isDarkMode={isDarkMode} setView={setView} setHistoryKind={setHistoryKind} remainingAttempts={attemptsLoading || !usage ? null : usage.remaining}/>)
              : <HistoryTab
                  isDarkMode={isDarkMode}
                  sessions={sessions}
                  selectedSession={selectedSession}
                  setSelectedSession={setSelectedSession}
                  onRefresh={fetchHistorySessions}
                  historyKind={historyKind}
                  setHistoryKind={setHistoryKind}
                  DetailComponent={historyKind === 'pitch' ? PitchDetailView : SessionDetailView}
                />
        )}
      </div>

      {!loading && (
        <>

        {debouncedView === 'history' && !selectedSession && totalPages > 1 && (
          <div className={`rounded-2xl border p-4 flex items-center justify-between ${
            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
          }`}>
            <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total} jobs
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isDarkMode 
                    ? 'border-slate-600 hover:bg-slate-700' 
                    : 'border-slate-300 hover:bg-slate-50'
                }`}
              >
                <ChevronLeft size={20} className={isDarkMode ? 'text-slate-300' : 'text-slate-700'} />
              </button>
                <span className={`text-sm px-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => goToPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    isDarkMode 
                      ? 'border-slate-600 hover:bg-slate-700' 
                      : 'border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <ChevronRight size={20} className={isDarkMode ? 'text-slate-300' : 'text-slate-700'} />
              </button>
            </div>
          </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

function ComingSoonDisplay({ isDarkMode }){
  return (
    <div className="flex flex-col items-center justify-center text-center p-10 md:p-16 min-h-[420px]">
      <div className={`mb-6 flex h-16 w-16 items-center justify-center rounded-full ${isDarkMode ? 'bg-blue-400/10' : 'bg-blue-500/10'}`}>
        <Clock size={30} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} />
      </div>

      <div className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-4 border ${
        isDarkMode ? 'bg-blue-400/10 text-blue-400 border-blue-400/30' : 'bg-blue-500/10 text-blue-700 border-blue-500/30'
      }`}>
        <Sparkles size={12} />
        Coming Soon
      </div>

      <h2 className={`text-xl md:text-2xl font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
        This feature is under construction
      </h2>
      <p className={`text-sm md:text-base max-w-md ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
        We're still building this part of Mock Interview. Check back soon &mdash; it'll be ready before you know it.
      </p>
    </div>
  )
}

function MainTab({isDarkMode, setView, setHistoryKind, remainingAttempts}){
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [showPreInterviewModal,   setShowPreInterviewModal] = useState(false);

  const [jobType, setJobType] = useState(''); // value matches InterviewQuestions.JobType on the backend
  // 'random' | 'custom'. Pick-from-Bank is a per-slot choice inside Custom (see the *Source state),
  // not a separate top-level mode.
  const [mode, setMode] = useState('');
  const [voice, setVoice] = useState('random'); // 'random' | 'male' | 'female'
  // Programs (from an organization) with custom interview scoring the student can choose.
  const [interviewPrograms, setInterviewPrograms] = useState([]);
  const [programId, setProgramId] = useState('');

  useEffect(() => {
    api.get('/org/my/evaluation-programs?type=interview')
      .then((res) => setInterviewPrograms(res.data.programs || []))
      .catch(() => setInterviewPrograms([]));
  }, []);

  const [bankQuestions, setBankQuestions] = useState({ 'behavioral': [], 'role-specific': [], 'leadership': [] });
  const [bankLoading, setBankLoading] = useState(false);
  const [sessionSetUpLoading, setSessionSetUpLoading] = useState(false);
  const [bankError, setBankError] = useState('');

  const [behaviorQ1ID, setBehaviorQ1ID] = useState('');
  const [behaviorQ2ID, setBehaviorQ2ID] = useState('');
  const [roleSpecificQID, setRoleSpecificQID] = useState('');
  const [leadershipQID, setLeadershipQID] = useState('');

  const [behaviorQ1Custom, setBehaviorQ1Custom] = useState('');
  const [behaviorQ2Custom, setBehaviorQ2Custom] = useState('');
  const [roleSpecificCustom, setRoleSpecificCustom] = useState('');
  const [leadershipCustom, setLeadershipCustom] = useState('');

  // Per-slot source (Custom mode): each of the 4 questions is independently 'custom' or 'bank'.
  const [behaviorQ1Source, setBehaviorQ1Source] = useState('custom'); // 'custom' | 'bank'
  const [behaviorQ2Source, setBehaviorQ2Source] = useState('custom');
  const [roleSpecificSource, setRoleSpecificSource] = useState('custom');
  const [leadershipSource, setLeadershipSource] = useState('custom');

  const textareaClass = `mt-2 w-full sm:w-64 text-sm rounded-lg border px-2 py-1.5 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-white border-slate-300 text-slate-700 placeholder-slate-400'}`;

  const handleStartSession = async () => {

    setSessionSetUpLoading(true)

    // Open synchronously (pre-await) so Safari doesn't treat this as a popup and block it
    const sessionTab = window.open('', '_blank');

    const questions = mode === "random" ? [] : [
      {category: "behavioral", source: behaviorQ1Source, questionDetail: behaviorQ1Source === "bank" ? behaviorQ1ID : behaviorQ1Custom},
      {category: "behavioral", source: behaviorQ2Source, questionDetail: behaviorQ2Source === "bank" ? behaviorQ2ID : behaviorQ2Custom},
      {category: "role-specific", source: roleSpecificSource, questionDetail: roleSpecificSource  === "bank" ? roleSpecificQID : roleSpecificCustom},
      {category: "leadership", source: leadershipSource, questionDetail: leadershipSource === "bank" ? leadershipQID : leadershipCustom},
    ]

    const payload = {
      jobType: jobType,
      mode: mode,
      voice: voice,
      questionSet: questions,
      programId: programId || undefined,
    }
    try{
      // create session
      const response = await api.post("/interview/sessions", payload)
      const sessionID = await response.data.sessionID
      const startTime = await response.data.startTime

      // redirect the pre-opened tab now that we have the session ID
      if (sessionTab) {
        sessionTab.location.href = `/interviewSession?session=${sessionID}`;
      } else {
        window.location.href = `/interviewSession?session=${sessionID}`;
      }
      setHistoryKind('interview');
      setView('history');
      setShowPreInterviewModal(false);
    } catch (err) {
      console.log("Error fetching Start Session", err)
      sessionTab?.close();
    } finally{
      setSessionSetUpLoading(false)
    }
  }

  useEffect(() => {
    if (mode !== 'custom' || !jobType) return;

    let cancelled = false;
    setBankLoading(true);
    setBankError('');
    setBehaviorQ1ID('');
    setBehaviorQ2ID('');
    setRoleSpecificQID('');
    setLeadershipQID('');

    api.get('/interview/questions/bank', { params: { job_type: jobType } })
      .then((res) => {
        if (cancelled) return;
        const grouped = { 'behavioral': [], 'role-specific': [], 'leadership': [] };
        (res.data?.questions || []).forEach((q) => {
          if (grouped[q.Category]) grouped[q.Category].push(q);
        });
        setBankQuestions(grouped);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching question bank:', err);
        setBankError('Could not load questions from the bank. Try again.');
      })
      .finally(() => {
        if (!cancelled) setBankLoading(false);
      });

    return () => { cancelled = true; };
  }, [mode, jobType]);

  const modeButtonClass = (active) => `px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
    active
      ? 'bg-blue-600 text-white'
      : (isDarkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
  }`;

  const sourceButtonClass = (active) => `px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
    active
      ? (isDarkMode ? 'bg-blue-400/20 text-blue-400 border border-blue-400/40' : 'bg-blue-500/10 text-blue-700 border border-blue-500/40')
      : (isDarkMode ? 'bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700' : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100')
  }`;

  const categoryLabelClass = (category) => `text-xs font-bold uppercase tracking-wide pt-1 min-w-0 ${categoryTextColor(category, isDarkMode)}`;

  const renderBankSelect = (category, value, onChange, occupiedID) => (
    <QuestionDropdown
      value={value}
      onChange={onChange}
      options={bankQuestions[category]}
      disabled={bankLoading || !jobType}
      placeholder={!jobType ? 'Select a job type first' : bankLoading ? 'Loading questions...' : '-- Select a question --'}
      isDarkMode={isDarkMode}
      occupiedID={occupiedID || null}
    />
  );

  const questionsStep = (
    <>
        <div className="flex gap-2 mb-4">
          <button type="button" onClick={() => setMode('random')} className={modeButtonClass(mode === 'random')}>Random / Matched</button>
          <button type="button" onClick={() => setMode('custom')} className={modeButtonClass(mode === 'custom')}>Custom</button>
        </div>

        {!mode && (
          <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Choose how you'd like your questions selected.</p>
        )}

        {mode === 'random' && (
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            We'll pick 2 behavioral, 1 role-specific, and 1 leadership question matched to your job type when the session starts.
          </p>
        )}

        {mode === 'custom' && (
          <div className="sm:ml-5 space-y-4">
            {bankError && <p className="text-sm text-red-500">{bankError}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_4fr] items-start gap-2 sm:gap-5">
              <h4 className={categoryLabelClass('behavioral')}>Behavioral 1:</h4>
              <div>
                <div className="flex gap-2 mb-2">
                  <button 
                    type="button" 
                    onClick={() => {
                      setBehaviorQ1Source('custom');
                      setBehaviorQ1ID('')
                    }} 
                    className={sourceButtonClass(behaviorQ1Source === 'custom')}
                  >
                    Write custom
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setBehaviorQ1Source('bank');
                      setBehaviorQ1Custom('');
                    }} 
                    className={sourceButtonClass(behaviorQ1Source === 'bank')}
                  >
                    Pick from bank
                  </button>
                </div>
                {behaviorQ1Source === 'bank' ? renderBankSelect('behavioral', behaviorQ1ID, setBehaviorQ1ID, behaviorQ2ID) : (
                  <textarea
                    value={behaviorQ1Custom}
                    onChange={(e) => setBehaviorQ1Custom(e.target.value)}
                    rows={3}
                    placeholder="Type your custom Behavioral-1 question..."
                    className={textareaClass}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_4fr] items-start gap-2 sm:gap-5">
              <h4 className={categoryLabelClass('behavioral')}>Behavioral 2: </h4>
              <div>
                <div className="flex gap-2 mb-2">
                  <button 
                    type="button" 
                    onClick={() => {
                      setBehaviorQ2Source('custom');
                      setBehaviorQ2ID('');
                    }} 
                    className={sourceButtonClass(behaviorQ2Source === 'custom')}
                  >
                    Write custom
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setBehaviorQ2Source('bank');
                      setBehaviorQ2Custom('');
                    }} 
                    className={sourceButtonClass(behaviorQ2Source === 'bank')}
                  >
                    Pick from bank
                  </button>
                </div>
                {behaviorQ2Source === 'bank' ? renderBankSelect('behavioral', behaviorQ2ID, setBehaviorQ2ID, behaviorQ1ID) : (
                  <textarea
                    value={behaviorQ2Custom}
                    onChange={(e) => setBehaviorQ2Custom(e.target.value)}
                    rows={3}
                    placeholder="Type your custom Behavioral-2 question..."
                    className={textareaClass}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_4fr] items-start gap-2 sm:gap-5">
              <h4 className={categoryLabelClass('role-specific')}>Role-specific: </h4>
              <div>
                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={() => setRoleSpecificSource('custom')} className={sourceButtonClass(roleSpecificSource === 'custom')}>Write custom</button>
                  <button type="button" onClick={() => setRoleSpecificSource('bank')} className={sourceButtonClass(roleSpecificSource === 'bank')}>Pick from bank</button>
                </div>
                {roleSpecificSource === 'bank' ? renderBankSelect('role-specific', roleSpecificQID, setRoleSpecificQID) : (
                  <textarea
                    value={roleSpecificCustom}
                    onChange={(e) => setRoleSpecificCustom(e.target.value)}
                    rows={3}
                    placeholder="Type your custom Role-specific question..."
                    className={textareaClass}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_4fr] items-start gap-2 sm:gap-5">
              <h4 className={categoryLabelClass('leadership')}>Leadership: </h4>
              <div>
                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={() => setLeadershipSource('custom')} className={sourceButtonClass(leadershipSource === 'custom')}>Write custom</button>
                  <button type="button" onClick={() => setLeadershipSource('bank')} className={sourceButtonClass(leadershipSource === 'bank')}>Pick from bank</button>
                </div>
                {leadershipSource === 'bank' ? renderBankSelect('leadership', leadershipQID, setLeadershipQID) : (
                  <textarea
                    value={leadershipCustom}
                    onChange={(e) => setLeadershipCustom(e.target.value)}
                    rows={3}
                    placeholder="Type your custom Leadership question..."
                    className={textareaClass}
                  />
                )}
              </div>
            </div>
          </div>
        )}
    </>
  );

  const questionsComplete = mode === 'random'
    ? true
    : mode === 'custom'
      ? [
          [behaviorQ1Source, behaviorQ1ID, behaviorQ1Custom],
          [behaviorQ2Source, behaviorQ2ID, behaviorQ2Custom],
          [roleSpecificSource, roleSpecificQID, roleSpecificCustom],
          [leadershipSource, leadershipQID, leadershipCustom],
        ].every(([source, bankValue, customValue]) => source === 'bank' ? !!bankValue : customValue.trim() !== '')
      : false;

  const canStart = !!jobType && questionsComplete && isReady;
  const outOfAttempts = remainingAttempts != null && remainingAttempts <= 0; // null = still loading, not zero

  // Cascading locks — each step stays locked until all earlier steps are done.
  const step2Locked = !jobType;
  const step3Locked = step2Locked || !questionsComplete;
  const step4Locked = step3Locked || !voice;

  return (
    <div className="relative p-6 md:p-10 max-w-3xl mx-auto">
      <div
        className={`space-y-6 transition-[filter] ${outOfAttempts ? 'blur-sm pointer-events-none select-none' : ''}`}
        aria-hidden={outOfAttempts}
      >
      <div>
        <h2 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
          This is the setup page for your mock interview session
        </h2>
        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          Work through each step below, then start your session when you're ready.
        </p>
      </div>

      <SetupStepCard step={1} title="Your Job Type" isDarkMode={isDarkMode}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 sm:justify-center">
          <SquareOptionCard
            value="technical"
            label="Technical"
            Icon={Code2}
            description="Roles centered on coding, systems, and technical problem-solving (e.g. Software Developer, Data Analyst, IT/DevOps)."
            selected={jobType === 'technical'}
            onSelect={setJobType}
            isDarkMode={isDarkMode}
          />
          <SquareOptionCard
            value="non-technical"
            label="Non-technical"
            Icon={Users}
            description="Roles centered on people, process, and business outcomes in tech companies (e.g. Product Manager, Sales Engineer, Marketing, HR)."
            selected={jobType === 'non-technical'}
            onSelect={setJobType}
            isDarkMode={isDarkMode}
          />
          <SquareOptionCard
            value="business"
            label="Business Roles"
            Icon={Briefcase}
            description="Roles in non-tech industries - finance, retail, consulting, healthcare (e.g. Financial Analyst, Operations Manager, Consultant, Account Executive)."
            selected={jobType === 'business'}
            onSelect={setJobType}
            isDarkMode={isDarkMode}
          />
        </div>
      </SetupStepCard>

      <SetupStepCard step={2} title="Select or Customize Your Questions" isDarkMode={isDarkMode} locked={step2Locked}>
        {questionsStep}
      </SetupStepCard>

      <SetupStepCard step={3} title="Interviewee Voice" isDarkMode={isDarkMode} locked={step3Locked}>
        <div className="flex gap-2 sm:gap-4 justify-center mb-4">
          <SquareOptionCard value="random" label="Random" Icon={Shuffle} selected={voice === 'random'} onSelect={setVoice} isDarkMode={isDarkMode} alwaysSquare />
          <SquareOptionCard value="male" label="Male" Icon={Mars} selected={voice === 'male'} onSelect={setVoice} isDarkMode={isDarkMode} alwaysSquare />
          <SquareOptionCard value="female" label="Female" Icon={Venus} selected={voice === 'female'} onSelect={setVoice} isDarkMode={isDarkMode} alwaysSquare />
        </div>
        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          <b>Note:</b> If the selected voice is unavailable for a bank question, the system will automatically fall back to an available voice.
        </p>
      </SetupStepCard>

      <SetupStepCard step={4} title="Mic Check" isDarkMode={isDarkMode} locked={step4Locked}>
        <MicTester isDarkMode={isDarkMode} setIsReady={setIsReady}/>
      </SetupStepCard>

      {interviewPrograms.length > 0 && (
        <div className={`rounded-2xl border p-4 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <label htmlFor="interview-eval-program" className={`block text-sm font-semibold mb-1 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
            Scoring criteria
          </label>
          <select
            id="interview-eval-program"
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
            className={`w-full text-sm rounded-lg border px-3 py-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'bg-white border-slate-300 text-slate-700'}`}
          >
            <option value="">Build My Biz Brand standard scoring</option>
            {interviewPrograms.map((p) => (
              <option key={p.ProgramID} value={p.ProgramID}>{p.ProgramName} ({p.OrgName}): {p.EvaluationName}</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex justify-center pt-2">
        <button
          type="button"
          disabled={!canStart || outOfAttempts}
          onClick={() => setShowPreInterviewModal(true)}
          className={`px-8 py-3 rounded-xl text-sm font-semibold transition-colors ${
            canStart && !outOfAttempts
              ? 'bg-primary-600 hover:bg-primary-500 text-white'
              : (isDarkMode ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-slate-200 text-slate-400 cursor-not-allowed')
          }`}
        >
          Ready to Start Your Session
        </button>
      </div>
      </div>

      {outOfAttempts && (
        <div className="absolute inset-0 z-10 flex items-start justify-center p-6">
          <div className={`sticky top-24 max-w-md text-center rounded-2xl border shadow-xl p-6 ${
            isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
          }`}>
            <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${
              isDarkMode ? 'bg-red-400/10' : 'bg-red-500/10'
            }`}>
              <Clock size={22} className={isDarkMode ? 'text-red-400' : 'text-red-600'} />
            </div>
            <h3 className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
              You've reached your attempt limit
            </h3>
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              You have reached your attempt limit. Your limit will reset when your subscription renews.
            </p>
          </div>
        </div>
      )}

      <PreInterviewModal
        isOpen={showPreInterviewModal}
        onClose={() => setShowPreInterviewModal(false)}
        isDarkMode={isDarkMode}
        sessionSetUpLoading={sessionSetUpLoading}
        onStart={handleStartSession}
      />
    </div>
  )
}

// Custom listbox for Pick-from-Bank: a native <select><option> can't wrap long question text, so this
// is a button + positioned panel where each row wraps across lines.
function QuestionDropdown({ value, options, onChange, disabled, placeholder, isDarkMode, occupiedID }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // options can change out from under an open panel (job type switch) — close rather than show stale.
  useEffect(() => {
    setOpen(false);
  }, [options]);

  const selected = options.find((o) => String(o.QuestionID) === String(value));

  return (
    <div ref={containerRef} className="relative w-full sm:w-64">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 text-sm rounded-lg border px-2 py-1.5 text-left ${
          disabled
            ? (isDarkMode ? 'bg-slate-800/50 border-slate-700 text-slate-500 cursor-not-allowed' : 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed')
            : (isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-200 hover:border-slate-500' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400')
        }`}
      >
        <span className="truncate">{selected ? selected.QuestionText : placeholder}</span>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
      </button>

      {open && !disabled && (
        <div
          role="listbox"
          className={`absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border shadow-lg ${
            isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-300'
          }`}
        >
          {options.length === 0 && (
            <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No questions available</div>
          )}
          {options.map((q) => (
            <div
              key={q.QuestionID}
              role="option"
              aria-selected={String(value) === String(q.QuestionID)}
              onClick={() => { 
                if (String(occupiedID) === String(q.QuestionID)){
                  return;
                }
                onChange(String(q.QuestionID)); 
                setOpen(false);
              }}
              className={`px-3 py-2 text-sm leading-snug cursor-pointer ${String(occupiedID) === String(q.QuestionID) ? "opacity-30" : ''} ${
                String(value) === String(q.QuestionID)
                  ? (isDarkMode ? 'bg-blue-400/10 text-blue-400' : 'bg-blue-500/10 text-blue-700')
                  : (isDarkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-100')
                }`
              }
            >
              {q.QuestionText}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryTab({ isDarkMode, sessions, selectedSession, setSelectedSession, onRefresh, historyKind, setHistoryKind, DetailComponent }){
  const headerRowClass = `hidden md:grid grid-cols-[1.3fr_0.8fr_1fr_1fr] gap-4 px-6 py-3 text-xs font-bold uppercase tracking-wide ${
    isDarkMode ? 'bg-slate-900/60 text-slate-400 border-b border-slate-700' : 'bg-slate-50 text-slate-500 border-b border-slate-200'
  }`;

  const dataRowClass = `grid grid-cols-2 md:grid-cols-[1.3fr_0.8fr_1fr_1fr] gap-y-2 gap-x-3 md:gap-4 px-4 md:px-6 py-4 cursor-pointer transition-colors ${
    isDarkMode ? 'border-b border-slate-700/60 hover:bg-slate-700/30' : 'border-b border-slate-100 hover:bg-slate-50'
  }`;
  const cellLabelClass = `md:hidden text-[10px] font-bold uppercase tracking-wide ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`;
  const cellValueClass = `text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`;

  // onReturnToList re-fetches the list before closing the detail view; onBack just closes it.
  if (selectedSession) {
    return (
      <DetailComponent
        session={selectedSession}
        onBack={() => setSelectedSession(null)}
        onReturnToList={() => { onRefresh?.(); setSelectedSession(null); }}
        isDarkMode={isDarkMode}
      />
    );
  }

  if (sessions.length === 0) {
    return (
      <div>
        <div className={`p-10 text-center text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {historyKind === 'pitch'
            ? 'No past elevator pitch sessions yet — completed sessions will show up here.'
            : 'No past interview sessions yet — completed sessions will show up here.'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={headerRowClass}>
        <div>Role</div>
        <div>Score</div>
        <div>Date</div>
        <div>Status</div>
      </div>

      {sessions.map((session) => (
        <div
          key={session.id}
          className={dataRowClass}
          onClick={() => setSelectedSession(session)}
        >
          <div className={`font-semibold text-sm ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{session.role}</div>
          <div>
            <span className={cellLabelClass}>Score </span>
            <span className={cellValueClass}>{session.score != null ? `${session.score}/100` : '—'}</span>
          </div>
          <div>
            <span className={cellLabelClass}>Date </span>
            <span className={cellValueClass}>{session.date}</span>
          </div>
          <div>
            <StatusBadge status={session.status} isDarkMode={isDarkMode} />
          </div>
        </div>
      ))}
    </div>
  )
}

// Full-screen detail view for one past session. Fetches GET /interview/sessions/:id for the
// per-question responses/audio the list endpoint doesn't include.
function SessionDetailView({ session, onBack, onReturnToList, isDarkMode }) {
  const [detail, setDetail] = useState(null); // full session from the detail endpoint (has `.responses`)
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [retryUnlocked, setRetryUnlocked] = useState(false);

  useEffect(() => {
    if (!session) return;

    let cancelled = false;
    setDetail(null);
    setDetailLoading(true);
    setDetailError('');
    api.get(`/interview/sessions/${session.id}`)
      .then((res) => {
        if (cancelled) return;
        setDetail(res.data?.session || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching session detail:', err);
        setDetailError("Could not load this session's details. Try again.");
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => { cancelled = true; };
  }, [session]);

  // Retry on a still-processing session is only allowed once it has genuinely
  // stalled (>= 5 min since its last status write). Unlock the button when that
  // point is reached while the detail page is open.
  useEffect(() => {
    setRetryUnlocked(false);
    const secs = Number(detail?.SecondsSinceUpdate);
    if (!Number.isFinite(secs)) return;
    const STUCK_SECONDS = 300;
    if (secs >= STUCK_SECONDS) { setRetryUnlocked(true); return; }
    const t = setTimeout(() => setRetryUnlocked(true), (STUCK_SECONDS - secs) * 1000);
    return () => clearTimeout(t);
  }, [detail]);

  const responses = detail?.responses || [];

  const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
  const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
  const body = isDarkMode ? 'text-slate-300' : 'text-slate-700';
  const card = isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const pill = (score100) => (isDarkMode ? PILL_DARK : PILL_LIGHT)[scoreTone(score100)];

  const retryButtonClass = `px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
    isDarkMode ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
  }`;
  const handleRetry = async () => {
    onBack();
    const res = await api.post(`interview/sessions/${session.id}/retry`);
    if (res.error) {
      console.log(`Retry failed: ${res.detail}`);
    }
  };

  // overall_feedback shape comes from RESPONSES_ANALYSIS_PROMPT in ai_service.py; overall_score is 0-100.
  const overallFeedback = detail?.AnalyzedJSON?.overall_feedback;
  const overallScore100 = Number.isFinite(overallFeedback?.overall_score) ? Math.round(overallFeedback.overall_score) : null;
  // overall_strengths / overall_improvement_focus may come back as an array or a single string.
  const toList = (v) => Array.isArray(v) ? v.filter(Boolean) : (typeof v === 'string' && v.trim() ? [v.trim()] : []);
  const overallStrengths = toList(overallFeedback?.overall_strengths);
  const overallImprovements = toList(overallFeedback?.overall_improvement_focus);
  const categoryRows = categoryBreakdownRows(overallFeedback);

  // Print-ready window; the browser's print dialog has a "Save as PDF" target.
  const handleDownloadPdf = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

    // tone -> hex, matching the on-screen colors.
    const TONE = {
      green: { bg: '#d1fae5', fg: '#065f46', bar: '#10b981' },
      yellow: { bg: '#fef3c7', fg: '#92400e', bar: '#f59e0b' },
      red: { bg: '#ffe4e6', fg: '#9f1239', bar: '#f43f5e' },
      blue: { bg: '#dbeafe', fg: '#1e40af', bar: '#3b82f6' },
    };
    const tone = (score) => TONE[scoreTone(score)] || TONE.blue;
    const CAT_HEX = { behavioral: '#059669', 'role-specific': '#ca8a04', leadership: '#dc2626' };

    const bar = (score) => score == null ? '' :
      `<div class="bar-bg"><div class="bar" style="width:${score}%;background:${tone(score).bar}"></div></div>`;
    const pill = (score, text) => {
      const t = tone(score);
      return `<span class="pill" style="background:${t.bg};color:${t.fg}">${esc(text)}</span>`;
    };
    // markers keep the section accent color; bullet text stays neutral.
    const bulletList = (items, markerColor) =>
      `<ul style="color:${markerColor}">${items.map((x) => `<li><span style="color:#1e293b">${esc(x)}</span></li>`).join('')}</ul>`;

    const responseCards = responses.map((r) => {
      const s = normalizeResponseScore(r.Feedback?.score ?? r.Score);
      const rs = toList(r.Feedback?.strengths);
      const ri = toList(r.Feedback?.areas_for_improvement);
      const catColor = CAT_HEX[r.Category] || '#475569';
      const starRows = r.Category === 'behavioral' ? starComponentRows(r.StarBreakdown) : [];
      const starScore = Number.isFinite(r.StarBreakdown?.star_score) ? Math.round(r.StarBreakdown.star_score) : null;
      const starHtml = starRows.length ? `
          <div style="border-top:1px solid #e2e8f0;margin-top:10px;padding-top:10px">
            <p class="label" style="color:#1d4ed8;margin:0 0 8px">STAR Breakdown</p>
            <div style="display:flex;align-items:center;gap:16px">
              <div>
                ${starRows.map(({ key, label, score100 }) => `
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
                    <span style="width:14px;font-size:11px;font-weight:700;color:#0f172a">${esc(label.charAt(0))}</span>
                    <div class="bar-bg" style="width:128px;margin:0">
                      <div class="bar" style="width:${score100 ?? 0}%;background:${tone(score100).bar}"></div>
                    </div>
                    <span style="font-size:10px;font-weight:700;padding:1px 6px;border-radius:99px;background:#e2e8f0;color:#475569">${score100 != null ? score100 : '—'}</span>
                  </div>`).join('')}
              </div>
              <div style="flex-shrink:0;position:relative;width:96px;height:96px">
                <svg width="96" height="96" viewBox="0 0 200 200">
                  <circle cx="100" cy="100" r="76" fill="none" stroke="#e2e8f0" stroke-width="22" />
                  ${starScore != null ? `<circle cx="100" cy="100" r="76" fill="none" stroke="${tone(starScore).bar}" stroke-width="22" stroke-linecap="round" stroke-dasharray="${(starScore / 100) * STAR_DONUT_C} ${STAR_DONUT_C}" transform="rotate(-90 100 100)" />` : ''}
                </svg>
                <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:800;color:#0f172a">${starScore != null ? starScore : '—'}</div>
              </div>
            </div>
            <div style="margin-top:10px">
              ${starRows.map(({ key, label, note, feedback }) => (note || feedback) ? `
                <div style="margin-top:6px">
                  <p style="font-size:12px;font-weight:700;margin:0;color:#0f172a">${esc(label)}</p>
                  ${note ? `<p class="muted" style="margin:2px 0">${esc(note)}</p>` : ''}
                  ${feedback ? `<p style="margin:2px 0;font-size:13px">${esc(feedback)}</p>` : ''}
                </div>` : '').join('')}
            </div>
          </div>` : (r.Category === 'behavioral'
        ? `<p style="margin-top:10px;color:#94a3b8;opacity:0.6;font-style:italic;font-size:13px">STAR analysis is unavailable for this response</p>`
        : '');
      return `
        <div class="card">
          <div class="rowhead">
            <h3 style="color:${catColor}">${esc(CATEGORY_LABELS_HISTORY[r.Category] || r.Category)}</h3>
            ${s != null ? pill(s, `${s}/100 · ${interviewRating(s)}`) : ''}
          </div>
          ${bar(s)}
          <p class="q">${esc(r.QuestionText)}</p>
          ${r.TranscriptText ? `<p class="label">Transcript</p><p class="muted">${esc(r.TranscriptText)}</p>` : '<p class="label">Transcript</p><p class="muted"><i>Nothing to transcribe - no speech detected</i></p>'}
          ${rs.length ? `<p class="label" style="color:#059669">Strengths</p>${bulletList(rs, '#065f46')}` : ''}
          ${ri.length ? `<p class="label" style="color:#b45309">Performance Gaps</p>${bulletList(ri, '#92400e')}` : ''}
          ${r.Feedback?.feedback_summary ? `<p class="label" style="color:#1d4ed8">Feedback</p><p>${esc(r.Feedback.feedback_summary)}</p>` : ''}
          ${starHtml}
        </div>`;
    }).join('');

    const allowedGeneralCat = ['communication', 'clarity', 'confidence', 'leadership', 'problem solving', 'business impact'];
    const categoryRowsHtml = categoryBreakdownRows(overallFeedback)
      .filter(({ label }) => allowedGeneralCat.includes(label.toLowerCase()))
      .map(({ label, score100 }) => `
        <div class="catrow">
          <div class="rowhead"><h3>${esc(label)}</h3>${score100 != null ? pill(score100, `${score100}/100`) : ''}</div>
          ${bar(score100)}
        </div>`).join('');

    const t = tone(overallScore100);

    w.document.write(`<html><head><title>Interview Analysis Results — ${esc(session.role)}</title>
      <style>
        *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{font-family:Arial,sans-serif;max-width:800px;margin:0 auto;padding:24px;color:#1e293b}
        h1{font-size:24px;margin-bottom:4px;color:#0f172a}
        h2{font-size:18px;margin:18px 0 8px}h3{font-size:15px;margin:0}
        .meta{color:#64748b;font-size:13px}
        .card{border:1px solid #e2e8f0;border-radius:12px;padding:16px;margin-bottom:12px}
        .overall{border-left:5px solid ${t.bar}}
        .score{font-size:36px;font-weight:800;color:${t.bar}}.score span{font-size:16px;color:#64748b}
        .pill{display:inline-block;padding:2px 10px;border-radius:99px;font-size:12px;font-weight:700}
        .rowhead{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:6px}
        .q{font-weight:600;margin:8px 0;color:#0f172a}
        .label{text-transform:uppercase;font-size:10px;letter-spacing:1px;font-weight:700;margin:10px 0 2px;color:#64748b}
        .muted{color:#64748b;font-size:13px}
        .bar-bg{height:8px;border-radius:8px;background:#f1f5f9;margin:6px 0 10px}
        .bar{height:8px;border-radius:8px}
        .catrow{margin-bottom:14px}.catrow:last-child{margin-bottom:0}
        .catrow .rowhead{margin-bottom:4px}.catrow .bar-bg{margin:0}
        ul{margin:2px 0;padding-left:18px;font-size:13px}
        @media print{body{padding:12px}}
      </style></head><body>
      <h1>Interview Analysis Results</h1>
      <p class="meta">${esc(session.role)} &middot; ${esc(session.date)}</p>
      <div class="card overall">
        <div class="score">${overallScore100 != null ? overallScore100 : '—'}<span>/100</span></div>
        ${pill(overallScore100, interviewRating(overallScore100))}
        ${bar(overallScore100)}
        <p style="font-weight:700;margin-top:4px;color:#0f172a">${esc(interviewHeadline(overallScore100))}</p>
        ${overallFeedback?.overall_summary ? `<p>${esc(overallFeedback.overall_summary)}</p>` : ''}
      </div>
      ${overallStrengths.length ? `<h2 style="color:#059669">Where you stand out</h2>${bulletList(overallStrengths, '#065f46')}` : ''}
      ${overallImprovements.length ? `<h2 style="color:#b45309">Where to focus next</h2>${bulletList(overallImprovements, '#92400e')}` : ''}
      <h2 style="color:#0f172a">Score breakdown</h2>
      ${responseCards || '<p class="muted">No responses recorded for this session.</p>'}
      ${categoryRowsHtml ? `<h2 style="color:#0f172a">Category breakdown</h2><div class="card">${categoryRowsHtml}</div>` : ''}
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className={`p-4 md:p-6 ${isDarkMode ? 'bg-slate-900' : 'bg-slate-100'}`}>
      <div className="flex items-center justify-between gap-3 mb-5">
        <button
          type="button"
          onClick={onReturnToList || onBack}
          className={`flex items-center gap-2 text-sm font-medium transition-colors ${
            isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <ArrowLeft size={16} />
          Back to History
        </button>

        {!detailLoading && !detailError && session.status === 'done' && detail && (
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            Download PDF
          </button>
        )}
      </div>

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <h1 className={`min-w-0 text-xl sm:text-2xl md:text-3xl font-extrabold ${heading}`}>Interview Analysis Results</h1>
          <StatusBadge status={session.status} isDarkMode={isDarkMode} />
        </div>

        {detailLoading && (
          <div className="flex items-center justify-center gap-2 py-16">
            <Loader2 size={20} className={`animate-spin ${sub}`} />
            <span className={`text-sm ${sub}`}>Loading session details...</span>
          </div>
        )}

        {!detailLoading && detailError && (
          <div className={`rounded-2xl border p-5 ${card}`}>
            <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{detailError}</p>
          </div>
        )}

        {!detailLoading && !detailError && session.status === 'error' && (
          <div className={`rounded-2xl border p-5 space-y-3 ${card}`}>
            <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              Something went wrong while analyzing this session. Try starting a new one.
            </p>
            <button type="button" onClick={handleRetry} className={retryButtonClass}>Retry</button>
          </div>
        )}

        {!detailLoading && !detailError && (session.status === 'transcribing' || session.status === 'loading') && (
          <div className={`rounded-2xl border p-8 flex flex-col items-center text-center gap-3 ${card}`}>
            <Loader2 size={32} className={`animate-spin ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <p className={`text-base font-bold ${heading}`}>Processing your responses</p>
            <p className={`text-sm max-w-md ${body}`}>
              Your answers are being scored right now. This usually takes a few minutes — come back to this
              session in a bit and your full report will be here.
            </p>
            <button
              type="button"
              onClick={handleRetry}
              disabled={!retryUnlocked}
              title={retryUnlocked ? '' : 'Available once the session has been stuck for 5 minutes'}
              className={`mt-1 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                retryUnlocked
                  ? (isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
                  : 'opacity-50 cursor-not-allowed ' + (isDarkMode ? 'bg-slate-800 text-slate-500' : 'bg-slate-100 text-slate-400')
              }`}
            >
              Retry
            </button>
            {!retryUnlocked && (
              <p className={`text-xs ${sub}`}>Stuck? Retry unlocks after 5 minutes with no progress.</p>
            )}
          </div>
        )}

        {/* 'incomplete' is for sessions with questions still unanswered; finished-but-still-scoring sessions map to 'loading'. */}
        {!detailLoading && !detailError && session.status === 'incomplete' && (
          <div className={`rounded-2xl border p-5 space-y-3 ${card}`}>
            <p className={`text-sm ${body}`}>
              This session isn&apos;t finished. If you still have questions left, pick up where you left off.
            </p>
            <button
              type="button"
              onClick={() => window.open(`/interviewSession?session=${session.id}`, '_blank')}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-primary-600 hover:bg-primary-500 text-white transition-colors"
            >
              Continue Session
            </button>
          </div>
        )}

        {!detailLoading && !detailError && session.status === 'done' && (
          <>
            {overallFeedback && (
              <div className={`rounded-2xl border p-5 md:p-6 ${card}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="shrink-0">
                    <div className={`text-4xl font-extrabold leading-none ${heading}`}>
                      {overallScore100 != null ? overallScore100 : '—'}
                      <span className={`text-lg font-semibold ${sub}`}>/100</span>
                    </div>
                    <span className={`inline-block mt-2 text-xs font-semibold px-2.5 py-1 rounded-full ${pill(overallScore100)}`}>
                      {interviewRating(overallScore100)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className={`text-lg font-bold ${heading}`}>{interviewHeadline(overallScore100)}</p>
                    {overallFeedback.overall_summary && (
                      <p className={`text-sm mt-1 leading-relaxed ${body}`}>{overallFeedback.overall_summary}</p>
                    )}
                  </div>
                </div>

                <div className={`flex gap-8 mt-4 pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>Job Type</p>
                    <p className={`text-xl font-bold ${heading}`}>{session.role}</p>
                  </div>
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide ${sub}`}>Date</p>
                    <p className={`text-xl font-bold ${heading}`}>{session.date}</p>
                  </div>
                </div>
              </div>
            )}

            {overallStrengths.length > 0 && (
              <section className={`rounded-2xl border p-5 ${card}`}>
                <h2 className={`text-lg font-bold mb-3 ${heading}`}>Where you stand out</h2>
                <ul className={`text-sm space-y-1.5 ${body}`}>
                  {overallStrengths.map((s, i) => <li key={i}>&#9656; {s}</li>)}
                </ul>
              </section>
            )}

            {overallImprovements.length > 0 && (
              <section className={`rounded-2xl border p-5 ${card}`}>
                <h2 className={`text-lg font-bold mb-3 ${heading}`}>Where to focus next</h2>
                <ul className={`text-sm space-y-1.5 ${body}`}>
                  {overallImprovements.map((s, i) => <li key={i}>&#9656; {s}</li>)}
                </ul>
              </section>
            )}

            <section>
              <h2 className={`text-lg font-bold mb-3 ${heading}`}>Score breakdown</h2>
              {responses.length === 0 && (
                <p className={`text-sm ${sub}`}>No responses recorded for this session.</p>
              )}
              <div className="space-y-3">
                {responses.map((r) => {
                  const score100 = normalizeResponseScore(r.Feedback?.score ?? r.Score);
                  const tone = scoreTone(score100);
                  const rStrengths = toList(r.Feedback?.strengths);
                  const rImprovements = toList(r.Feedback?.areas_for_improvement);
                  return (
                    <div key={r.ResponseID} className={`rounded-2xl border p-4 ${card}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <span className={`text-[11px] font-bold uppercase tracking-wide ${categoryTextColor(r.Category, isDarkMode)}`}>
                            {CATEGORY_LABELS_HISTORY[r.Category] || r.Category}
                          </span>
                          <h3 className={`text-sm font-bold mt-1 ${heading}`}>{r.QuestionText}</h3>
                        </div>
                        {score100 != null && (
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${pill(score100)}`}>
                            {score100}/100 &middot; {interviewRating(score100)}
                          </span>
                        )}
                      </div>

                      {score100 != null && (
                        <div className={`h-2 rounded-full overflow-hidden mb-3 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <div className={`h-full rounded-full ${BAR[tone]}`} style={{ width: `${score100}%` }} />
                        </div>
                      )}

                      <div className="space-y-3">
                        {r.responseAudioURL ? (
                          <AudioPlayer src={apiMediaUrl(r.responseAudioURL)} isDarkMode={isDarkMode} />
                        ) : (
                          <p className={`text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No recording available.</p>
                        )}

                        <div className={`border-l-2 pl-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                          <p className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${sub}`}>Transcript</p>
                          {r.TranscriptText ? 
                          <p className={`text-xs leading-relaxed ${sub}`}>{r.TranscriptText}</p> :
                          <p className={`italic text-xs leading-relaxed ${sub}`}>Nothing to transcribe - no speech detected</p> }
                        </div>

                        {rStrengths.length > 0 && (
                          <div>
                            <p className={`text-xs font-bold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-primary-300' : 'text-primary-700'}`}>Strengths</p>
                            <ul className={`text-sm space-y-1 ${body}`}>
                              {rStrengths.map((s, i) => (
                                <li key={i}><span className={isDarkMode ? 'text-primary-400' : 'text-primary-600'}>&#9656;</span> {s}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {rImprovements.length > 0 && (
                          <div>
                            <p className={`text-xs font-bold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Performance Gaps</p>
                            <ul className={`text-sm space-y-1 ${body}`}>
                              {rImprovements.map((s, i) => (
                                <li key={i}><span className={isDarkMode ? 'text-amber-400' : 'text-amber-600'}>&#9656;</span> {s}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {r.Feedback?.feedback_summary && (
                          <div>
                            <p className={`text-xs font-bold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Feedback</p>
                            <p className={`text-sm leading-relaxed ${body}`}>{r.Feedback.feedback_summary}</p>
                          </div>
                        )}

                        {(() => {
                          if (r.Category !== 'behavioral') return null;
                          const starRows = starComponentRows(r.StarBreakdown);
                          if (starRows.length === 0) {
                            // Covers every "nothing to show" case alike (STAR analysis failed for
                            // the whole session, or this response failed its own relevance gate) --
                            // a quiet, clearly-secondary note rather than surfacing an error, so a
                            // STAR hiccup never looks like the rest of the report is broken.
                            return (
                              <div className={`pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                <p className={`text-xs italic opacity-60 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                  STAR analysis is unavailable for this response
                                </p>
                              </div>
                            );
                          }
                          const starScore = Number.isFinite(r.StarBreakdown?.star_score)
                            ? Math.round(r.StarBreakdown.star_score) : null;
                          return (
                            <div className={`pt-3 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                              <p className={`text-xs font-bold uppercase tracking-wide mb-3 ${isDarkMode ? 'text-primary-300' : 'text-primary-700'}`}>
                                STAR Breakdown
                              </p>
                              <div className="flex items-center gap-6">
                                <div className="space-y-2">
                                  {starRows.map(({ key, label, score100 }) => (
                                    <div key={key} className="flex items-center gap-2">
                                      <span className={`w-3.5 shrink-0 text-xs font-bold ${heading}`}>{label.charAt(0)}</span>
                                      {/* Fixed width (not flex-1) so the donut's size — at least 1/3 of
                                          this width — stays a guaranteed ratio regardless of card width. */}
                                      <div className={`h-1.5 w-32 shrink-0 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                                        <div className={`h-full rounded-full ${BAR[scoreTone(score100)]}`} style={{ width: `${score100 ?? 0}%` }} />
                                      </div>
                                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
                                        isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'
                                      }`}>
                                        {score100 != null ? score100 : '—'}
                                      </span>
                                    </div>
                                  ))}
                                </div>

                                <div className="shrink-0 relative" style={{ width: 96, height: 96 }}>
                                  <svg width="96" height="96" viewBox="0 0 200 200">
                                    <circle cx="100" cy="100" r="76" fill="none" stroke={isDarkMode ? '#334155' : '#e2e8f0'} strokeWidth="22" />
                                    {starScore != null && (
                                      <circle cx="100" cy="100" r="76" fill="none" strokeWidth="22" strokeLinecap="round"
                                        className={BAR[scoreTone(starScore)].replace('bg-', 'text-')} stroke="currentColor"
                                        strokeDasharray={`${(starScore / 100) * STAR_DONUT_C} ${STAR_DONUT_C}`}
                                        transform="rotate(-90 100 100)" />
                                    )}
                                  </svg>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <span className={`text-lg font-extrabold ${heading}`}>{starScore != null ? starScore : '—'}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-3 space-y-2">
                                {starRows.map(({ key, label, note, feedback }) => (note || feedback) && (
                                  <div key={key}>
                                    <p className={`text-xs font-semibold ${heading}`}>{label}</p>
                                    {note && <p className={`text-xs leading-relaxed ${sub}`}>{note}</p>}
                                    {feedback && <p className={`text-xs leading-relaxed mt-0.5 ${body}`}>{feedback}</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {categoryRows.length > 0 && (
              <section className={`rounded-2xl border p-5 ${card}`}>
                <h2 className={`text-lg font-bold mb-4 ${heading}`}>Category breakdown</h2>
                <div className="space-y-4">
                  {categoryRows.map(({ key, label, score100 }) => {
                    const tone = scoreTone(score100);
                    const allowedGeneralCat = ['communication', 'clarity', 'confidence', 'leadership', 'problem solving', 'business impact'];
                    return (
                      allowedGeneralCat.includes(label.toLowerCase()) &&
                      (<div key={key}>
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                          <span className={`text-sm font-semibold ${heading}`}>{label}</span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${pill(score100)}`}>
                            {score100 != null ? `${score100}/100` : '—'}
                          </span>
                        </div>
                        <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <div className={`h-full rounded-full ${BAR[tone]}`} style={{ width: `${score100 ?? 0}%` }} />
                        </div>
                      </div>)
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PreInterviewModal({ isOpen, onClose, onStart, isDarkMode, sessionSetUpLoading }) {
  const steps = [
    "You'll be asked 4 questions total: 2 behavioral, 1 role-specific, and 1 leadership question.",
    "Each question is read aloud to you before you answer — no need to read anything yourself.",
    "You'll have a soft 20-minute timer for the whole session. It's a guideline, not a hard cutoff.",
    "Your spoken answers are recorded through your mic, which is already confirmed working.",
    "Scoring and feedback are generated after the session ends, not live, so don't worry about pausing to think.",
    "Your results are private to you and are not shared with coaches or anyone else.",
  ];

  return (
    <InterviewModal
      isOpen={isOpen}
      onClose={sessionSetUpLoading ? () => {} : onClose} // block closing mid-request
      isDarkMode={isDarkMode}
      title={sessionSetUpLoading ? 'Setting Up Session' : 'Before You Start'}
      footer={sessionSetUpLoading ? null : (
        <>
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              isDarkMode ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onStart}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-500 transition-colors"
          >
            Start
          </button>
        </>
      )}
    >
      {sessionSetUpLoading ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <Loader2 size={48} className={`animate-spin ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`} />
          <p className={`text-lg font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Setting Up Session</p>
          <p className={`text-sm text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Hang tight while we get your questions and interviewer ready...
          </p>
        </div>
      ) : (
        <>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Here's what to expect before you begin your mock interview session:
          </p>
          <ul className="space-y-3">
            {steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className={`mt-0.5 flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                  isDarkMode
                    ? 'bg-primary-400/10 text-primary-400 border border-primary-400/40'
                    : 'bg-primary-500/10 text-primary-600 border border-primary-500/50'
                }`}>
                  {i + 1}
                </span>
                <span className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>{step}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </InterviewModal>
  );
}

